// frontend/src/components/auth/impersonationText.ts
/** SA-906: every sentence the View-as surfaces print, in one place. A server
 * code is never printed raw. */
import type { ImpersonationEndReason } from '@/stores/authStore'

/** `expiresAt` is the server's ISO stamp; a naive one is UTC (the backend's
 * `_utcnow()` is naive UTC). */
export function parseExpiry(expiresAt: string): number {
  const hasZone = /(?:Z|[+-]\d{2}:\d{2})$/.test(expiresAt)
  return Date.parse(hasZone ? expiresAt : `${expiresAt}Z`)
}

export function remainingText(expiresAt: string, nowMs: number): string {
  const left = parseExpiry(expiresAt) - nowMs
  if (!(left > 0)) return 'ending'
  const minutes = Math.floor(left / 60_000)
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `ends in ${h} h ${m} m` : `ends in ${m} m`
}

export function endedText(reason: ImpersonationEndReason): string | null {
  switch (reason) {
    case 'stopped':
      return null
    case 'expired':
      return 'View-as ended: the session expired.'
    case 'unauthorized':
      return 'View-as ended: the user can no longer sign in.'
  }
}

const REFUSALS: Record<string, string> = {
  target_is_self: 'You cannot view as yourself.',
  target_is_admin: 'An administrator cannot be viewed as.',
  target_is_service_account: 'A service account cannot be viewed as.',
  target_inactive: 'A deactivated user cannot be viewed as.',
  user_not_found: 'That user no longer exists.',
  target_has_more_access: 'This user holds permissions you do not.',
  actor_not_admin: 'View as is for administrators only.',
}

export function refusalText(code: string | undefined): string {
  return (code && REFUSALS[code]) || 'View as is not available for this user.'
}

/** SA-922, re-worded in SA-1034 PR 5 so the banner and the credential refusal say the same thing. */
export const KEYS_UNAVAILABLE = 'Voice and your API keys are unavailable while viewing as another user.'

/** A per-user credential route refused under a View-as token (`view_as_forbidden`). The keys are the person's own. */
export function viewAsForbiddenText(): string {
  return KEYS_UNAVAILABLE
}
